// repo/primary-care-connect/frontend/src/pages/Portal/OhtAdmin/OhtAdminLayout.tsx

import React, { useEffect, useState } from "react";
import { NavLink, Outlet, Navigate } from "react-router-dom";
import { getOhtProfile, type OhtProfile } from "../../../api/ohtAdmin";
import "./OhtAdminLayout.css";

type BootstrapRole = {
  roleName: string;
  isActive?: boolean | number | string | null;
};

type BootstrapResponse = {
  userId?: number;
  status?: string;
  roles?: BootstrapRole[];
  primaryRole?: string | null;
};

type OhtContext = {
  ohtId: number | null;
  ohtName: string;
  ohtCode: string;
  profile: OhtProfile | null;
};

function normRoleName(value: unknown): string {
  return String(value || "").trim().toLowerCase();
}

function isRoleActive(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  return String(value).trim().toLowerCase() === "true";
}

function getBootstrapRoles(data: BootstrapResponse | null): BootstrapRole[] {
  return Array.isArray(data?.roles) ? data.roles : [];
}

function userHasOhtAdminRole(data: BootstrapResponse | null): boolean {
  const activeRoles = getBootstrapRoles(data).filter((role) =>
    isRoleActive(role.isActive)
  );

  return activeRoles.some((role) => normRoleName(role.roleName) === "ohtadmin");
}

function userHasMultipleActiveRoles(data: BootstrapResponse | null): boolean {
  const activeRoles = getBootstrapRoles(data).filter((role) =>
    isRoleActive(role.isActive)
  );

  return activeRoles.length > 1;
}

async function loadBootstrap(
  signal: AbortSignal
): Promise<BootstrapResponse | null> {
  const res = await fetch("/api/portal-auth-bootstrap", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    signal,
  });

  if (!res.ok) return null;

  return res.json();
}

async function loadAuthEmail(signal: AbortSignal): Promise<string | null> {
  try {
    const resp = await fetch("/.auth/me", {
      credentials: "include",
      signal,
    });

    if (!resp.ok) return null;

    const json = await resp.json();
    const principal = Array.isArray(json?.clientPrincipal)
      ? json.clientPrincipal[0]
      : json?.clientPrincipal;

    const details = principal?.userDetails;
    return typeof details === "string" && details.includes("@") ? details : null;
  } catch {
    return null;
  }
}

export default function OhtAdminLayout() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [denied, setDenied] = useState(false);
  const [oht, setOht] = useState<OhtContext | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [hasMultipleRoles, setHasMultipleRoles] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    let mounted = true;

    async function loadPortalContext() {
      setLoading(true);
      setError(null);
      setDenied(false);

      try {
        const [bootstrap, profile, authEmail] = await Promise.all([
          loadBootstrap(controller.signal),
          getOhtProfile(controller.signal),
          loadAuthEmail(controller.signal),
        ]);

        if (!mounted) return;

        if (bootstrap && !userHasOhtAdminRole(bootstrap)) {
          setDenied(true);
          return;
        }

        setOht({
          ohtId: profile.ohtId ?? null,
          ohtName: profile.name || "OHT Portal",
          ohtCode: profile.code || "",
          profile,
        });

        setHasMultipleRoles(userHasMultipleActiveRoles(bootstrap));
        setEmail(profile.currentUser?.email || authEmail);
      } catch (err: any) {
        if (!mounted || err?.name === "AbortError") return;

        const status = Number(err?.status || err?.response?.status || 0);
        if (status === 401 || status === 403) {
          setDenied(true);
          return;
        }

        setError(String(err?.message || "Failed to load OHT portal context."));
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadPortalContext();

    return () => {
      mounted = false;
      controller.abort();
    };
  }, []);

  useEffect(() => {
    function handleResize() {
      if (window.innerWidth > 820) setMobileMenuOpen(false);
    }

    function handleKey(event: KeyboardEvent) {
      if (event.key === "Escape") setMobileMenuOpen(false);
    }

    window.addEventListener("resize", handleResize);
    document.addEventListener("keydown", handleKey);

    return () => {
      window.removeEventListener("resize", handleResize);
      document.removeEventListener("keydown", handleKey);
    };
  }, []);

  function handleSignOut() {
    const postLogout = `${window.location.origin}/`;
    window.location.href = `/.auth/logout?post_logout_redirect_uri=${encodeURIComponent(
      postLogout
    )}`;
  }

  function closeMobile() {
    setMobileMenuOpen(false);
  }

  function toggleMobile() {
    setMobileMenuOpen((value) => !value);
  }

  if (denied) {
    return <Navigate to="/portal" replace />;
  }

  if (loading) {
    return (
      <div
        className="oht-admin-loading"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 12,
          alignItems: "center",
          padding: "40px 24px",
          color: "#102a43",
        }}
      >
        Loading OHT portal…
      </div>
    );
  }

  if (error) {
    return (
      <div className="oht-admin-error">
        <h2>Access problem</h2>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="oht-admin-shell">
      <aside
        className={`oht-admin-sidebar${
          mobileMenuOpen ? " oht-admin-sidebar--mobile-open" : ""
        }`}
      >
        <div className="oht-admin-sidebar-header">
          <div className="oht-admin-sidebar-mobile-bar">
            <div>
              <div className="oht-admin-oht-name">
                {oht?.ohtName || "OHT Portal"}
              </div>
              <div className="oht-admin-role">
                OHT Admin Portal
                {oht?.ohtCode ? ` · ${oht.ohtCode}` : ""}
              </div>
            </div>

            <button
              type="button"
              className="oht-admin-mobile-menu-button"
              aria-expanded={mobileMenuOpen}
              aria-controls="oht-admin-portal-nav"
              aria-label={
                mobileMenuOpen
                  ? "Collapse navigation menu"
                  : "Expand navigation menu"
              }
              onClick={toggleMobile}
            >
              <span className="oht-admin-mobile-menu-button-text">Menu</span>
              <span
                className={
                  mobileMenuOpen
                    ? "oht-admin-mobile-menu-button-chevron oht-admin-mobile-menu-button-chevron--open"
                    : "oht-admin-mobile-menu-button-chevron"
                }
                aria-hidden="true"
              >
                ▾
              </span>
            </button>
          </div>
        </div>

        <div
          id="oht-admin-portal-nav"
          className={
            mobileMenuOpen
              ? "oht-admin-sidebar-accordion oht-admin-sidebar-accordion--open"
              : "oht-admin-sidebar-accordion"
          }
        >
          <nav className="oht-admin-nav">
            <div className="oht-admin-nav-section">
              <div className="oht-admin-nav-label">Workspace</div>

              <NavLink
                to="/portal/oht-admin"
                end
                className={({ isActive }) =>
                  isActive ? "nav-item active" : "nav-item"
                }
                onClick={closeMobile}
              >
                Overview
              </NavLink>

              <NavLink
                to="/portal/oht-admin/clinics"
                className={({ isActive }) =>
                  isActive ? "nav-item active" : "nav-item"
                }
                onClick={closeMobile}
              >
                Clinics
              </NavLink>

              <NavLink
                to="/portal/oht-admin/doctors"
                className={({ isActive }) =>
                  isActive ? "nav-item active" : "nav-item"
                }
                onClick={closeMobile}
              >
                Doctors &amp; Capacity
              </NavLink>

              <NavLink
                to="/portal/oht-admin/insights"
                className={({ isActive }) =>
                  isActive ? "nav-item active" : "nav-item"
                }
                onClick={closeMobile}
              >
                Reports
              </NavLink>
            </div>

            <div className="oht-admin-nav-section">
              <div className="oht-admin-nav-label">Operations</div>

              <NavLink
                to="/portal/oht-admin/profile"
                className={({ isActive }) =>
                  isActive ? "nav-item active" : "nav-item"
                }
                onClick={closeMobile}
              >
                Profile
              </NavLink>

              <NavLink
                to="/portal/oht-admin/messages"
                className={({ isActive }) =>
                  isActive ? "nav-item active" : "nav-item"
                }
                onClick={closeMobile}
              >
                Messages
              </NavLink>
            </div>

            {email ? (
              <div className="oht-admin-signedin">
                <div className="oht-admin-signedin-label">Signed in as</div>
                <div className="oht-admin-signedin-email">{email}</div>
              </div>
            ) : null}

            <div className="oht-admin-sidebar-bottom">
              <NavLink
                to="/public"
                className={({ isActive }) =>
                  isActive ? "nav-item active" : "nav-item"
                }
                onClick={closeMobile}
              >
                Public Site
              </NavLink>

              {hasMultipleRoles ? (
                <NavLink
                  to="/portal/select-role"
                  className={({ isActive }) =>
                    isActive ? "nav-item active" : "nav-item"
                  }
                  onClick={closeMobile}
                >
                  Switch Role
                </NavLink>
              ) : null}

              <button
                type="button"
                className="nav-item nav-item-primary"
                onClick={() => {
                  closeMobile();
                  handleSignOut();
                }}
              >
                Sign Out
              </button>
            </div>
          </nav>
        </div>
      </aside>

      <main className="oht-admin-main">
        <Outlet context={{ oht }} />
      </main>
    </div>
  );
}

export type { OhtContext };